import {Link} from "react-router-dom";
import React, { useRef, useState } from "react";
import {NavItem, NavLeafItem, NavParentItem, NavSection, PathConfig, resolvePath} from "./config/navbar.config";
import {ChevronDown, ChevronsLeft, ChevronsRight} from "lucide-react";
import {UserRole} from "../../core/roles/roles.type";


interface NavigationMenuViewProps {
    role: UserRole;
    activePath: string;
    collapsed: boolean;
    mobileOpen: boolean;
    sections: NavSection[];
    openParents: Set<string>;
    handleNavItemClick: (path: string) => void;
    handleParentClick: (id: string) => void;
    handleCollapseToggle: () => void;
}

function NavigationMenuView(props: NavigationMenuViewProps) {
    const isCompact = props.collapsed && !props.mobileOpen;
    const isLeaf = (item: NavItem): item is NavLeafItem => !item.children;
    const isParent = (item: NavItem): item is NavParentItem => !!item.children;



    return (
        <nav
            className={`pn-sidebar${isCompact ? " pn-sidebar--collapsed" : ""}
            ${props.mobileOpen ? " pn-sidebar--mobile-open" : ""}`}
        >
            <Link to={"/dashboard"} className="pn-sidebar__logo">
                <img
                    src={`${process.env.REACT_APP_IMAGE_BASE_URL}/icons/people-nest-logo.svg`}
                    alt={"People Nest Logo"}
                    className={"pn-sidebar__logo-image"}
                />
                {!isCompact && <span className="pn-sidebar__logo-text">PeopleNest</span>}
            </Link>

            <div className="pn-sidebar__nav" >
                {props.sections.map((section: NavSection, sectionIndex: number) => (
                    <div key={section.label} style={{'--item-index': sectionIndex} as React.CSSProperties}>
                        {/*{!isCompact && <div className="pn-nav-section-label">{section.label}</div>}*/}
                        {section.items.map((item: NavItem, itemIndex: number) => {
                            const Icon = item.icon;
                            const itemStyle = {'--item-index': itemIndex} as React.CSSProperties;

                            if (isLeaf(item)) {
                                const path = resolvePath(item.path, props.role);
                                const isActive = props.activePath === path;
                                return (
                                    <button
                                        key={item.id}
                                        className={`pn-nav-item${isActive ? " pn-nav-item--active" : ""}`}
                                        onClick={() => props.handleNavItemClick(path)}
                                        title={isCompact ? item.label : ""}
                                        style={itemStyle}
                                    >
                                        <span className="pn-nav-item__icon">
                                            <Icon size={17} strokeWidth={1.75}/>
                                        </span>
                                        {!isCompact && (
                                            <>
                                                <span className="pn-nav-item__label">{item.label}</span>
                                                {item.badge && <span className="pn-nav-item__badge">{item.badge}</span>}
                                            </>
                                        )}
                                    </button>
                                );
                            }

                            if (isParent(item)) {
                                const isOpen = props.openParents.has(item.id);
                                const hasActiveChild = item.children.some(child =>
                                    props.activePath === resolvePath(child.path as PathConfig, props.role)
                                );

                                return (
                                    <div key={item.id} style={itemStyle}>
                                        <button
                                            className={`pn-nav-item pn-nav-item--parent${hasActiveChild ? " pn-nav-item--active" : ""}`}
                                            onClick={() => props.handleParentClick(item.id)}
                                            title={isCompact ? item.label : ""}
                                        >
                                            <span className="pn-nav-item__icon">
                                                <Icon size={17} strokeWidth={1.75}/>
                                            </span>
                                            {!isCompact && (
                                                <>
                                                    <span className="pn-nav-item__label">{item.label}</span>
                                                    <ChevronDown
                                                        size={14}
                                                        className={`pn-nav-item__chevron${isOpen ? " pn-nav-item__chevron--open" : ""}`}
                                                    />
                                                </>
                                            )}
                                        </button>
                                        {isOpen && !isCompact && (
                                            <div className="pn-nav-children">
                                                {item.children.map((child, childIndex) => {
                                                    const ChildIcon = child.icon;
                                                    const childPath = resolvePath(child.path as PathConfig, props.role);
                                                    const isActive = props.activePath === childPath;
                                                    const childStyle = {'--item-index': childIndex} as React.CSSProperties;
                                                    return (
                                                        <button
                                                            key={child.id}
                                                            className={`pn-nav-item pn-nav-item--child${isActive ? " pn-nav-item--active" : ""}`}
                                                            onClick={() => props.handleNavItemClick(childPath)}
                                                            style={childStyle}
                                                        >
                                                            <span className="pn-nav-item__icon">
                                                                <ChildIcon size={15} strokeWidth={1.75}/>
                                                            </span>
                                                            <span className="pn-nav-item__label">{child.label}</span>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                );
                            }

                            return null;
                        })}
                    </div>
                ))}
            </div>

            <div className="pn-sidebar__footer">
                <button
                    className="pn-sidebar__collapse-btn"
                    onClick={props.handleCollapseToggle}
                    title={isCompact ? "Expand sidebar" : "Collapse sidebar"}
                    aria-label={isCompact ? "Expand sidebar" : "Collapse sidebar"}
                >
                    {isCompact ? <ChevronsRight size={18}/> : <ChevronsLeft size={18}/>}
                    {/*{!props.collapsed && <span>Collapse</span>}*/}
                </button>
            </div>
        </nav>
    )
}

export default NavigationMenuView;
